import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GroupedBenefitsV2 } from '../BenefitsPreviewV2/GroupedBenefitsV2';
import {
  BenefitsPreviewCategoryContainerV2,
  StyledSavviTransluscentContainer,
} from './CategoriesV2ContainerStyled';
import { CategoriesV2Container } from './CategoriesV2Container';
import { CategoryResponseDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { PsButtonTuner } from 'stencilSetup';
import { ROUTES } from 'enrollment/app/routes';
import {
  organizeBundledBenefitCategoryBenefits,
  useBundledBenefitCategories,
} from 'enrollment/utils/bundledBenefitsUtils';

interface CategoriesProps {
  benefitCategoryData: CategoryResponseDto;
  isLoading: boolean;
}

interface ChosenBundleTopContentProps {
  buttonType?: 'primary' | 'secondary';
  buttonLabel?: string;
  header?: string;
  description?: string;
  solidBackground?: boolean;
}

export function ChosenBundleTopContent({
  buttonType = 'secondary',
  buttonLabel = 'exploreMoreBundlesButton',
  header = 'chosenBundleHeader',
  description = 'chosenBundleDescription',
  solidBackground,
}: ChosenBundleTopContentProps) {
  const { t } = useTranslation('pages.bundledBenefits');
  const navigate = useNavigate();
  const handleOnClick = () => {
    navigate(`/enrollment/${ROUTES.enrollment.decisionSupportRouter.path}`);
  };
  return (
    <StyledSavviTransluscentContainer
      data-testid="ChosenBundleTopContent"
      id="ChosenBundleTopContent"
      sx={
        solidBackground ? { backgroundColor: '#f7f1ff', marginTop: '20px' } : {}
      }
    >
      <div className="d-flex justify-content-between v-center">
        <div>
          <h3>{t(header)}</h3>
          <p className="body2">{t(description)}</p>
        </div>
        <PsButtonTuner
          buttonId="explore-bundles-button"
          data-testid="explore-bundles-button"
          label={t(buttonLabel)}
          level={buttonType}
          onClick={handleOnClick}
          extraClass={`text-nowrap mb-0 ${solidBackground ? '' : 'btn-transparent-bg'}`}
        />
      </div>
    </StyledSavviTransluscentContainer>
  );
}

export const BundledBenefitsCategories = ({
  benefitCategoryData,
  isLoading,
}: CategoriesProps) => {
  const { t } = useTranslation('pages.bundledBenefits');

  const bundledBenefitsCategoryBenefits =
    organizeBundledBenefitCategoryBenefits(
      benefitCategoryData.data.benefits,
      'chosenBundle',
      true,
      benefitCategoryData.data.bundled_org_benefit_ids
    );
  const otherBenefitsCategoryBenefits = organizeBundledBenefitCategoryBenefits(
    benefitCategoryData.data.benefits,
    'otherBundle',
    true,
    benefitCategoryData.data.bundled_org_benefit_ids
  );
  const sortedBenefits = [
    bundledBenefitsCategoryBenefits,
    otherBenefitsCategoryBenefits,
  ];
  const bundledCategories = useBundledBenefitCategories();
  const bundledBenefitCategories = bundledCategories.flatMap(
    (category) => category.lookup_code
  );
  const colors = ['annotation5', 'annotation3'];

  return (
    <div>
      {bundledBenefitCategories.map((benefitCategory, index) => {
        const color = colors[index];
        const benefitGroupName = t(benefitCategory);
        const benefitPreviews = GroupedBenefitsV2({
          benefitCategoryData: {
            ...benefitCategoryData,
            data: {
              ...benefitCategoryData.data,
              benefits: sortedBenefits[index],
            },
          },
          benefitCategory: 'benefits',
          currentBenefitCategory: 'benefits',
          color,
          loading: isLoading,
          bundledBenefitOverride: true, // Override for bundled benefits
        });

        const sortedBenefitPreviews = benefitPreviews.sort((a, b) => {
          const orderA = a?.props.benefitData.benefit.display_order || 0;
          const orderB = b?.props.benefitData.benefit.display_order || 0;

          return orderA - orderB;
        });

        const hasEligibleBenefit = sortedBenefitPreviews.find(
          (benefit) => benefit
        );
        const categoryTotal = sortedBenefits[index].reduce((acc, benefit) => {
          let val = 0;
          if (benefit.coverage) {
            val += Number(benefit.coverage.req_subscriber_premium);
            benefit.coverage.rider_coverages?.forEach((rider) => {
              val += Number(rider.req_subscriber_premium);
            });
            benefit.coverage.coverage_lines?.forEach((line) => {
              line.rider_coverage_lines.forEach((rider) => {
                val += Number(rider.req_subscriber_premium);
              });
            });
          }
          return acc + val;
        }, 0);

        if (hasEligibleBenefit) {
          return (
            <div key={index}>
              <CategoriesV2Container
                key={index}
                id={benefitGroupName}
                groupName={benefitGroupName}
                dataTestId="benefit-grouping-container"
                loading={isLoading && !hasEligibleBenefit}
                className="category-container"
                categoryTotal={categoryTotal}
                color={color}
                extraTopContent={
                  benefitCategory === 'otherBundle' ? undefined : (
                    <ChosenBundleTopContent />
                  )
                }
              >
                {benefitCategory === 'otherBundle' && (
                  <PSBannerWrapper
                    id="other-bundled-benefits-banner"
                    message={t('otherBundleDescription')}
                    level="info"
                    icon
                    extraClass="mb-3"
                  />
                )}
                <BenefitsPreviewCategoryContainerV2
                  id="preview-container"
                  className="m-0"
                >
                  {benefitPreviews}
                </BenefitsPreviewCategoryContainerV2>
              </CategoriesV2Container>
            </div>
          );
        } else {
          return <></>;
        }
      })}
    </div>
  );
};
